/**
 * CanvasEventController - Centralized canvas event handling via event delegation
 * 
 * Instead of attaching events to each device/port individually in renderDevice(),
 * this controller uses a single delegated listener that handles all canvas interactions.
 * 
 * Handles:
 * - Device header drag (mousedown/touchstart)
 * - Port interactions (mousedown/touchstart/touchend)
 * - Device selection (click)
 * - Device touch multi-select (touchend)
 * - Canvas background interactions (pan, box select)
 * - Context menu (contextmenu)
 */

import {
    getPointerPos,
    startLongPressTimer,
    cancelLongPress,
    isLongPressTriggered,
    resetLongPressTriggered,
    isTouchMultiSelectMode,
    isTouchPortMultiSelectMode,
    exitTouchMultiSelectMode,
    exitTouchPortMultiSelectMode,
    handleTouchMultiSelectTap,
    handleTouchPortMultiSelectTap,
    shouldPan,
    startCanvasPan,
    handleCanvasPan,
    endCanvasPan,
    getCanvasPanState,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    startSingleFingerTapTracking
} from './GestureController.js';

import {
    isDraggingDevice,
    hasDragMoved,
    startDeviceDrag,
    handleDeviceDrag,
    endDeviceDrag,
    bringToFront
} from './DragController.js';

import {
    isDraggingCable,
    startCableDrag,
    startMultiCableDrag,
    updateTempCable,
    finishCableDrag,
    setLastMousePosition
} from './CableDragController.js';

import {
    getSelectedDevices,
    setDeviceSelection,
    clearDeviceSelection,
    toggleDeviceInBoxSelection,
    startBoxSelection,
    handleBoxSelection,
    finishBoxSelection,
    getBoxDragState,
    setBoxDragState,
    clearBoxSelection,
    isPortSelected,
    handleRangeSelect,
    handleToggleSelect,
    clearMultiSelection,
    getMultiSelectState
} from './SelectionController.js';

import { showContextMenu, closeContextMenu } from './ContextMenuController.js';
import { selectConnection } from './CableRenderer.js';

// ============= MODULE STATE =============

// DOM references
let canvas = null;
let cablesSvg = null;
let canvasContainer = null;

// Dependencies (injected via init)
let instanceModel = null;
let definitionModel = null;
let connectionModel = null;
let state = null;
let selectDeviceFunc = null;
let updateSelectionButtonsFunc = null;
let renderLibraryFunc = null;
let screenToCanvasFunc = null;

// ============= HELPER FUNCTIONS =============

/**
 * Check if target is a background element (canvas, cablesSvg, or container)
 * Used to determine if clicks should trigger pan/box-select vs element interaction
 * @param {HTMLElement} target - DOM element to check
 * @returns {boolean} True if target is a canvas background element
 */
function isBackgroundElement(target) {
    return target === canvas || target === cablesSvg || target === canvasContainer;
}

/**
 * Extract port info from a port element's data attributes
 */
function getPortInfo(portEl) {
    return {
        deviceId: portEl.dataset.device,
        groupIndex: parseInt(portEl.dataset.group),
        type: portEl.dataset.type,
        index: parseInt(portEl.dataset.index),
        direction: portEl.dataset.direction
    };
}

/**
 * Get device from instance model by element ID
 */
function getDeviceById(deviceId) {
    return instanceModel.getById(deviceId);
}

/**
 * Get template for a device
 */
function getTemplateForDevice(device) {
    return definitionModel.getById(device.templateId);
}

// ============= EVENT HANDLERS =============

/**
 * Handle mousedown on port elements
 */
function handlePortMouseDown(e, portEl) {
    if (e.type === 'mousedown' && e.button !== 0) return;
    
    const portInfo = getPortInfo(portEl);
    const { direction, deviceId } = portInfo;
    
    // Output and insert ports can be multi-selected (they can send signal)
    // Input ports can also be used on router devices (for internal routing)
    let isInternalRoutingPort = false;
    if (direction !== 'output' && direction !== 'insert') {
        if (direction === 'input') {
            // Check if this is a router device - if so, allow internal routing
            const device = getDeviceById(deviceId);
            const template = device ? getTemplateForDevice(device) : null;
            if (template?.isRouter) {
                // Mark as internal routing port - continue with same multi-select logic
                isInternalRoutingPort = true;
            } else {
                // Non-router input ports return early
                return;
            }
        } else {
            // Other non-output/insert ports return early
            return;
        }
    }
    
    // Shift+click: range selection (works for both external and internal routing)
    if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleRangeSelect(portInfo, portEl);
        return;
    }
    
    // Ctrl/Cmd+click: toggle individual selection
    if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        handleToggleSelect(portInfo, portEl);
        return;
    }
    
    // Regular click on a selected port: start drag from multi-selection
    const multiSelectState = getMultiSelectState();
    if (isPortSelected(portInfo) && multiSelectState.selectedPorts.length > 0) {
        startMultiCableDrag(e, portEl);
        return;
    }
    
    // In touch port multi-select mode, let touchend handler deal with toggling
    // Don't start cable drag or clear selection - touchend will call handleTouchPortMultiSelectTap
    if (e.type === 'touchstart' && isTouchPortMultiSelectMode()) {
        return;
    }
    
    // Regular click: clear selection and start single cable drag
    clearMultiSelection();
    startCableDrag(e, portEl);
}

/**
 * Handle touchstart on port elements
 */
function handlePortTouchStart(e, portEl) {
    // Prevent iOS native context menu / text selection
    e.preventDefault();
    
    // Start long-press timer for output ports
    if (portEl.dataset.direction === 'output') {
        startLongPressTimer(e, portEl, 'port');
    }
    
    handlePortMouseDown(e, portEl);
}

/**
 * Handle touchend on port elements (for port multi-select mode)
 */
function handlePortTouchEnd(e, portEl) {
    if (!isDraggingCable() && !isLongPressTriggered() && isTouchPortMultiSelectMode()) {
        e.preventDefault();
        handleTouchPortMultiSelectTap(portEl);
    }
}

/**
 * Handle mousedown on device header (start drag)
 */
function handleDeviceHeaderMouseDown(e, deviceEl) {
    const deviceId = deviceEl.id;
    const device = getDeviceById(deviceId);
    if (!device) return;
    
    if (e.type === 'mousedown' && e.button !== 0) return;
    
    startDeviceDrag(e, device);
}

/**
 * Handle touchstart on device header
 */
function handleDeviceHeaderTouchStart(e, deviceEl) {
    // Prevent iOS native context menu / text selection
    e.preventDefault();
    
    // Start long-press timer for context menu
    startLongPressTimer(e, deviceEl, 'device');
    
    const deviceId = deviceEl.id;
    const device = getDeviceById(deviceId);
    if (device) {
        startDeviceDrag(e, device);
    }
}

/**
 * Handle click on device (selection)
 */
function handleDeviceClick(e, deviceEl) {
    // Don't handle if click was on a port
    if (e.target.closest('.port')) return;
    
    const deviceId = deviceEl.id;
    
    // Check for multi-selection modifiers
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
        toggleDeviceInBoxSelection(deviceId, e.shiftKey);
    } else {
        selectDeviceFunc(deviceId);
    }
}

/**
 * Handle touchend on device (for touch multi-select mode)
 */
function handleDeviceTouchEnd(e, deviceEl) {
    // Only handle if it was a tap (not a drag)
    // Check hasDragMoved() instead of isDraggingDevice() because drag state is set on touchstart
    if (!hasDragMoved() && !isLongPressTriggered() && isTouchMultiSelectMode()) {
        if (!e.target.closest('.port')) {
            e.preventDefault();
            const deviceId = deviceEl.id;
            handleTouchMultiSelectTap(deviceId);
        }
    }
}

/**
 * Handle canvas background mousedown (pan or box select)
 */
function handleCanvasBackgroundMouseDown(e) {
    if (e.button === 0 || e.button === 1) {
        if (shouldPan(e)) {
            e.preventDefault(); // Prevent middle-click scroll
            startCanvasPan(e);
        } else if (e.button === 0) {
            startBoxSelection(e, getPointerPos);
        }
    }
}

/**
 * Handle canvas background touchstart
 */
function handleCanvasBackgroundTouchStart(e) {
    e.preventDefault();
    
    // Track for double-tap detection
    startSingleFingerTapTracking(e);
    
    // Exit touch multi-select mode when tapping empty canvas
    if (isTouchMultiSelectMode()) {
        exitTouchMultiSelectMode();
        clearDeviceSelection();
        updateSelectionButtonsFunc();
        return;
    }
    
    // Exit port multi-select mode
    if (isTouchPortMultiSelectMode()) {
        exitTouchPortMultiSelectMode();
        return;
    }
    
    // Start long-press timer for context menu
    startLongPressTimer(e, e.target, 'canvas');
    
    // 1-finger on empty canvas = box select
    startBoxSelection(e, getPointerPos);
}

/**
 * Handle click on canvas (deselection)
 */
function handleCanvasClick(e) {
    if (e.target === canvas) {
        selectDeviceFunc(null);
        // Clear multi-selection when clicking on empty canvas
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            clearMultiSelection();
            // Note: box selection is cleared by finishBoxSelection or by clicking device
        }
    }
}

/**
 * Handle global pointer move events for all drag operations
 * Dispatches to appropriate handlers: device drag, cable drag, pan, box selection
 * @param {MouseEvent|TouchEvent} e - Pointer event
 */
function handlePointerMove(e) {
    const pos = getPointerPos(e);
    
    // Track position for Alt key preview refresh (always, not just during drag)
    setLastMousePosition({ clientX: pos.clientX, clientY: pos.clientY });
    
    if (isDraggingDevice()) {
        handleDeviceDrag(e);
    }
    
    if (isDraggingCable()) {
        updateTempCable(e);
    }
    
    if (getCanvasPanState()) {
        handleCanvasPan(e);
    }
    
    if (getBoxDragState()) {
        handleBoxSelection(e, getPointerPos);
    }
}

/**
 * Handle global pointer up events to end all drag operations
 * Cleans up device drag, cable drag, pan, and box selection states
 * @param {MouseEvent|TouchEvent} e - Pointer event
 */
function handlePointerUp(e) {
    cancelLongPress(); // Always cancel long-press timer on pointer up
    
    if (isDraggingDevice()) {
        endDeviceDrag(e);
    }
    
    if (isDraggingCable()) {
        finishCableDrag(e);
    }
    
    if (getCanvasPanState()) {
        endCanvasPan();
    }
    
    if (getBoxDragState()) {
        finishBoxSelection(e, getPointerPos, updateSelectionButtonsFunc);
    }
    
    // Reset long-press triggered flag
    resetLongPressTriggered();
}

/**
 * Handle contextmenu event (right-click on desktop, long-press on iOS)
 */
function handleContextMenu(e) {
    // Allow context menu on input fields for accessibility
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
    }
    
    const menu = document.getElementById('context-menu');
    
    // If we just showed context menu via long-press (touch), prevent native contextmenu
    if (isLongPressTriggered()) {
        e.preventDefault();
        resetLongPressTriggered();
        return;
    }
    
    // If our context menu is already visible, prevent native
    if (menu && menu.classList.contains('visible')) {
        e.preventDefault();
        return;
    }
    
    // Check if right-click is on a device
    const deviceEl = e.target.closest('.device');
    if (deviceEl) {
        e.preventDefault();
        const deviceId = deviceEl.id;
        
        // Select the device if not already selected
        const selectedDevices = getSelectedDevices();
        if (!selectedDevices.has(deviceId)) {
            setDeviceSelection(deviceId);
        }
        
        const pos = getPointerPos(e);
        showContextMenu(pos.clientX, pos.clientY, 'device', { deviceId });
        return;
    }
    
    // Check if right-click is on a cable
    if (e.target.tagName === 'path' && e.target.dataset.id) {
        e.preventDefault();
        const connectionId = e.target.dataset.id;
        selectConnection(connectionId);
        const pos = getPointerPos(e);
        showContextMenu(pos.clientX, pos.clientY, 'cable', { connectionId });
        return;
    }
    
    // Right-click on canvas (empty space)
    if (isBackgroundElement(e.target)) {
        e.preventDefault();
        const pos = getPointerPos(e);
        showContextMenu(pos.clientX, pos.clientY, 'canvas', { clickX: pos.clientX, clickY: pos.clientY });
    }
}

/**
 * Handle click on device body (clear multi-selection)
 */
function handleDocumentClick(e) {
    // If clicking on a device body (not a port) without modifiers, clear selection
    if (e.target.closest('.device') && !e.target.closest('.port') && 
        !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        clearMultiSelection();
    }
}

// ============= DELEGATED EVENT HANDLERS =============

/**
 * Delegated mousedown handler for canvas
 */
function onCanvasMouseDown(e) {
    // Check for port first (most specific)
    const portEl = e.target.closest('.port');
    if (portEl) {
        handlePortMouseDown(e, portEl);
        return;
    }
    
    // Check for device header (drag handle)
    const headerEl = e.target.closest('.device-header');
    if (headerEl) {
        const deviceEl = headerEl.closest('.device');
        if (deviceEl) {
            handleDeviceHeaderMouseDown(e, deviceEl);
        }
        return;
    }
    
    // Check for background (pan or box select)
    if (isBackgroundElement(e.target)) {
        handleCanvasBackgroundMouseDown(e);
    }
}

/**
 * Delegated touchstart handler for canvas
 */
function onCanvasTouchStart(e) {
    // First handle multi-touch gestures
    handleTouchStart(e);
    
    // For single touch, check targets
    if (e.touches.length === 1) {
        // Check for port first
        const portEl = e.target.closest('.port');
        if (portEl) {
            handlePortTouchStart(e, portEl);
            return;
        }
        
        // Check for device header
        const headerEl = e.target.closest('.device-header');
        if (headerEl) {
            const deviceEl = headerEl.closest('.device');
            if (deviceEl) {
                handleDeviceHeaderTouchStart(e, deviceEl);
            }
            return;
        }
        
        // Check for background
        if (isBackgroundElement(e.target)) {
            handleCanvasBackgroundTouchStart(e);
        }
    }
}

/**
 * Delegated touchend handler for canvas
 */
function onCanvasTouchEnd(e) {
    handleTouchEnd(e);
    
    // Check for port touchend (multi-select mode)
    const portEl = e.target.closest('.port');
    if (portEl) {
        handlePortTouchEnd(e, portEl);
        return;
    }
    
    // Check for device touchend (multi-select mode)
    const deviceEl = e.target.closest('.device');
    if (deviceEl) {
        handleDeviceTouchEnd(e, deviceEl);
    }
}

/**
 * Delegated click handler for canvas
 */
function onCanvasClick(e) {
    // Check for device click (not on port)
    const deviceEl = e.target.closest('.device');
    if (deviceEl && !e.target.closest('.port')) {
        handleDeviceClick(e, deviceEl);
        return;
    }
    
    // Check for empty canvas click
    if (e.target === canvas) {
        handleCanvasClick(e);
    }
}

// ============= INITIALIZATION =============

/**
 * Initialize the canvas event controller with dependencies
 * Sets up delegated event listeners for all canvas interactions
 * @param {Object} deps - Dependencies object
 * @param {Object} deps.instanceModel - Device instance model
 * @param {Object} deps.definitionModel - Device definition/template model
 * @param {Object} deps.connectionModel - Connection model
 * @param {Object} deps.state - Application state
 * @param {Function} deps.selectDevice - Function to select a device by ID
 * @param {Function} deps.updateSelectionButtons - Function to update UI selection buttons
 * @param {Function} deps.renderLibrary - Function to re-render the device library
 * @param {Function} deps.screenToCanvas - Function to convert screen to canvas coordinates
 */
export function initCanvasEventController(deps) {
    // Store dependencies
    instanceModel = deps.instanceModel;
    definitionModel = deps.definitionModel;
    connectionModel = deps.connectionModel;
    state = deps.state;
    selectDeviceFunc = deps.selectDevice;
    updateSelectionButtonsFunc = deps.updateSelectionButtons;
    renderLibraryFunc = deps.renderLibrary;
    screenToCanvasFunc = deps.screenToCanvas;
    
    // Get DOM references
    canvas = document.getElementById('canvas');
    cablesSvg = document.getElementById('cables-svg');
    canvasContainer = document.querySelector('.canvas-container');
    
    // ============= DELEGATED CANVAS EVENT LISTENERS =============
    
    // Canvas container gets the delegated listeners
    // Using container because it includes both canvas and cables SVG
    canvasContainer.addEventListener('mousedown', onCanvasMouseDown);
    canvasContainer.addEventListener('touchstart', onCanvasTouchStart, { passive: false });
    canvasContainer.addEventListener('touchend', onCanvasTouchEnd);
    canvasContainer.addEventListener('touchcancel', onCanvasTouchEnd);
    canvasContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    // Click needs separate handling for selection
    canvas.addEventListener('click', onCanvasClick);
    
    // ============= GLOBAL EVENT LISTENERS =============
    
    // Global move/up for drag operations (already exist, but we add them here for completeness)
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    
    // Context menu (right-click / long-press)
    document.addEventListener('contextmenu', handleContextMenu);
    
    // Document click for multi-selection clear
    document.addEventListener('click', handleDocumentClick);
}

// ============= EXPORTS =============

// Export handlePointerMove and handlePointerUp for external use (e.g., TouchController)
export { handlePointerMove, handlePointerUp };

// Export helper for checking background elements
export { isBackgroundElement };
